import type { InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios'
import axios from 'axios'
import {
  getAccessToken,
  getRefreshToken,
  saveAuthTokens,
  clearAuthTokens,
} from '../utils/tokenStorage'
import { useAuthStore } from '../../features/auth/hooks/useAuthStore'
import { getApiBaseUrl } from './apiClient'
import { getActiveAcademicYearId } from './academicYear'
import { showToastMessage } from '../../components/Toast'
import { resolveBranchId } from '../utils/branchUtils'

import { useAcademicYearStore } from '../stores/useAcademicYearStore'

export const DEFAULT_ACADEMIC_YEAR_ID = ''

let failedQueue: Array<{
  resolve: (token: string) => void
  reject: (err: unknown) => void
}> = []

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error)
    } else if (token) {
      promise.resolve(token)
    }
  })
  failedQueue = []
}

export const resetRefreshState = () => {
  processQueue(new Error('Auth session terminated'), null)
  failedQueue = []
}

/**
 * Singleton promise deduplication for cookie-gated token refresh
 * Prevents token reuse errors caused by strict refresh token rotation when concurrent calls fire
 */
let activeRefreshPromise: Promise<string | null> | null = null

const safeNavigateToLogin = () => {
  if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
    const isTestEnv = (import.meta as any).env?.MODE === 'test' || (typeof globalThis !== 'undefined' && (globalThis as any).process?.env?.NODE_ENV === 'test')
    if (!isTestEnv) {
      try {
        window.location.href = '/login'
      } catch {
        // Ignore jsdom navigation restrictions during test execution
      }
    }
  }
}

let lastSessionExpiredToastTime = 0

const showSessionExpiredToastOnce = () => {
  const now = Date.now()
  if (now - lastSessionExpiredToastTime > 10000) {
    lastSessionExpiredToastTime = now
    showToastMessage('Session expired. Please log in again.', 'error')
  }
}

export const handleTokenRefresh = async (): Promise<string | null> => {
  if (activeRefreshPromise) {
    console.info('[API Client] Refresh request already in flight. Subscribing to active singleton promise...')
    return activeRefreshPromise
  }

  activeRefreshPromise = (async () => {
    const currentBaseUrl = getApiBaseUrl()
    const storedAccessToken = getAccessToken()
    let rawRefToken = getRefreshToken() || ''
    if (!rawRefToken && typeof document !== 'undefined' && document.cookie) {
      const cookies = document.cookie.split(';')
      for (const cookie of cookies) {
        const parts = cookie.trim().split('=')
        if (parts.length >= 2) {
          const key = parts[0].trim().toLowerCase()
          const val = parts.slice(1).join('=').trim()
          if ((key === 'refreshtoken' || key === 'refresh_token' || key === 'token') && val) {
            rawRefToken = decodeURIComponent(val)
            break
          }
        }
      }
    }
    if (!rawRefToken && typeof localStorage !== 'undefined') {
      rawRefToken =
        localStorage.getItem('refresh_token') ||
        localStorage.getItem('refreshToken') ||
        localStorage.getItem('token') ||
        ''
    }
    
    const storedRefToken = rawRefToken || storedAccessToken || ''

    if (!storedRefToken && typeof document !== 'undefined' && !document.cookie) {
      console.warn('[API Client] No refresh token available. Session expired cleanly.')
      clearAuthTokens()
      useAuthStore.setState({ isAuthenticated: false, user: null, role: null, accessToken: null, permissions: [] })
      safeNavigateToLogin()
      throw new Error('No refresh token available')
    }

    const storedAcademicYear =
      (typeof localStorage !== 'undefined'
        ? localStorage.getItem('academic_year_id') ||
          localStorage.getItem('academic_session_id') ||
          localStorage.getItem('x_academic_year')
        : undefined) || undefined

    const refreshBody = storedRefToken
      ? { refreshToken: storedRefToken, refresh_token: storedRefToken, token: storedRefToken }
      : {}

    const refreshHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(storedRefToken
        ? {
            'x-refresh-token': storedRefToken,
            'X-Refresh-Token': storedRefToken,
            'x-token': storedRefToken,
            Authorization: `Bearer ${storedRefToken}`,
          }
        : {}),
      ...(storedAcademicYear && isUuidHeader(storedAcademicYear)
        ? {
            'X-Academic-Year': storedAcademicYear,
            'x-academic-year': storedAcademicYear,
          }
        : {}),
    }

    const refreshOptions = {
      withCredentials: true,
      headers: refreshHeaders,
    }

    let response: any = null

    try {
      console.info('[API Client] Rotating token via POST /api/auth/refresh...')
      response = await axios.post(`${currentBaseUrl}/api/auth/refresh`, refreshBody, refreshOptions)
    } catch (primaryErr: any) {
      const status = primaryErr.response?.status
      console.warn(`[API Client] Primary /api/auth/refresh failed (status: ${status}). Retrying without Authorization header...`)

      if (storedRefToken) {
        try {
          const fallbackHeaders = { ...refreshHeaders }
          delete fallbackHeaders.Authorization
          response = await axios.post(`${currentBaseUrl}/api/auth/refresh`, refreshBody, {
            ...refreshOptions,
            headers: fallbackHeaders,
          })
        } catch {
          // continue
        }
      }

      if (!response) {
        throw primaryErr
      }
    }

    const newAccessToken =
      response?.data?.accessToken ||
      response?.data?.token ||
      response?.data?.access_token ||
      response?.data?.data?.accessToken ||
      response?.data?.data?.token ||
      response?.data?.data?.access_token

    const newRefreshToken =
      response?.data?.refreshToken ||
      response?.data?.refresh_token ||
      response?.data?.token ||
      response?.data?.data?.refreshToken ||
      response?.data?.data?.refresh_token ||
      storedRefToken

    if (newAccessToken) {
      console.info('[API Client] Token refresh successful. Updating Zustand & LocalStorage.')
      saveAuthTokens(newAccessToken, newRefreshToken)
      const state = useAuthStore.getState()
      if (typeof state.setToken === 'function') {
        state.setToken(newAccessToken)
      } else if (typeof state.setAccessToken === 'function') {
        state.setAccessToken(newAccessToken)
      }
      return newAccessToken
    } else {
      throw new Error('No access token returned in refresh response')
    }
  })()

  try {
    const token = await activeRefreshPromise
    processQueue(null, token)
    return token
  } catch (err) {
    console.error('[API Client] Refresh token process failed. Session expired:', err)
    clearAuthTokens()
    useAuthStore.getState().logout()
    processQueue(err, null)
    showSessionExpiredToastOnce()
    safeNavigateToLogin()
    throw err
  } finally {
    activeRefreshPromise = null
  }
}

const isUuidHeader = (val?: string): boolean => {
  if (!val) return false
  const trimmed = val.trim()
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(trimmed) || trimmed.length > 25
}

export const isEmptyValue = (val: unknown): boolean => {
  if (val === null || val === undefined) return true
  if (typeof val === 'string') {
    const trimmed = val.trim().toLowerCase()
    return trimmed === '' || trimmed === 'null' || trimmed === 'undefined'
  }
  return false
}

export const sanitizeHeadersAndParams = (config: InternalAxiosRequestConfig): void => {
  if (config.headers) {
    const headersObj =
      typeof (config.headers as any).toJSON === 'function'
        ? (config.headers as any).toJSON()
        : config.headers

    for (const key of Object.keys(headersObj)) {
      const val = headersObj[key]
      if (isEmptyValue(val)) {
        if (typeof (config.headers as any).delete === 'function') {
          ;(config.headers as any).delete(key)
        }
        delete (config.headers as any)[key]
      }
    }

    const targetHeaders = [
      'x-branch-id',
      'X-Branch-Id',
      'x-academic-year',
      'X-Academic-Year',
      'x-session-id',
      'X-Session-Id',
      'x-org-id',
      'X-Org-Id',
    ]

    for (const hKey of targetHeaders) {
      const val = (config.headers as any)[hKey]
      if (val !== undefined && isEmptyValue(val)) {
        if (typeof (config.headers as any).delete === 'function') {
          ;(config.headers as any).delete(hKey)
        }
        delete (config.headers as any)[hKey]
      }
    }

    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      if (typeof (config.headers as any).delete === 'function') {
        ;(config.headers as any).delete('Content-Type')
        ;(config.headers as any).delete('content-type')
      } else {
        delete (config.headers as any)['Content-Type']
        delete (config.headers as any)['content-type']
      }
    }
  }

  if (config.params) {
    if (typeof URLSearchParams !== 'undefined' && config.params instanceof URLSearchParams) {
      const keysToDelete: string[] = []
      config.params.forEach((val, key) => {
        if (isEmptyValue(val)) {
          keysToDelete.push(key)
        }
      })
      keysToDelete.forEach((key) => (config.params as URLSearchParams).delete(key))
    } else if (typeof config.params === 'object' && config.params !== null) {
      for (const key of Object.keys(config.params)) {
        if (isEmptyValue(config.params[key])) {
          delete config.params[key]
        }
      }
    }
  }
}

export const requestInterceptor = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  const apiBaseUrl = getApiBaseUrl()

  // Handle embedded absolute URLs (e.g. S3 pre-signed file/receipt URLs passed directly or with /api/ prefix)
  if (config.url) {
    const fullMatch = config.url.match(/(https?:\/\/[^\s]+)/i)
    if (fullMatch) {
      config.url = fullMatch[1]
      config.baseURL = ''
    }
  }

  if (!config.baseURL && config.baseURL !== '') {
    config.baseURL = apiBaseUrl
  }

  if (config.headers) {
    config.headers['ngrok-skip-browser-warning'] = 'true'
  }

  if (config.headers && config.data instanceof FormData) {
    if (typeof config.headers.delete === 'function') {
      config.headers.delete('Content-Type')
      config.headers.delete('content-type')
    } else {
      delete config.headers['Content-Type']
      delete config.headers['content-type']
    }
  }

  const authState = useAuthStore.getState()
  const token =
    authState.token ||
    authState.accessToken ||
    getAccessToken() ||
    (typeof localStorage !== 'undefined' ? localStorage.getItem('access_token') || localStorage.getItem('auth_token') : null) ||
    (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('access_token') || sessionStorage.getItem('auth_token') : null)

  const isAbsoluteUrl = Boolean(config.url?.startsWith('http://') || config.url?.startsWith('https://'))
  let isExternal = false
  if (isAbsoluteUrl) {
    if (apiBaseUrl) {
      try {
        const configHost = new URL(config.url!).hostname.toLowerCase()
        const baseHost = new URL(apiBaseUrl).hostname.toLowerCase()
        isExternal = configHost !== baseHost
      } catch {
        isExternal = !config.url?.startsWith(apiBaseUrl)
      }
    } else {
      isExternal = true
    }
  }

  if (config.url && !isAbsoluteUrl && !config.url.startsWith('/api') && !config.url.startsWith('api/')) {
    config.url = `/api${config.url.startsWith('/') ? '' : '/'}${config.url}`
  }

  const isAcademicSessionsRequest = config.url?.includes('/api/academic-sessions')

  const isAuthOrRefreshEndpoint =
    config.url?.includes('/api/auth/login') ||
    config.url?.includes('/api/auth/refresh')

  if (config.headers && !isExternal) {
    if (token && !isAuthOrRefreshEndpoint && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`
    }

    const existingBranchId =
      (config.headers['x-branch-id'] as string | undefined) ||
      (config.headers['X-Branch-Id'] as string | undefined)

    let branchId = resolveBranchId(existingBranchId) || ''

    let orgId =
      localStorage.getItem('active_org_id') ||
      localStorage.getItem('org_id') ||
      localStorage.getItem('x_org_id') ||
      localStorage.getItem('orgId') ||
      localStorage.getItem('activeOrgId') ||
      ''

    if (!branchId || !orgId) {
      try {
        const storedUser = JSON.parse(localStorage.getItem('school-erp-user') || '{}')
        if (!branchId && storedUser?.branchId) {
          branchId = storedUser.branchId
        }
        if (!orgId && storedUser?.orgId) {
          orgId = storedUser.orgId
        }
      } catch {
        // ignore
      }
    }

    if (!orgId && token) {
      try {
        const parts = token.split('.')
        if (parts.length === 3) {
          const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')))
          if (payload?.orgId) {
            orgId = payload.orgId
          }
          if (!branchId && Array.isArray(payload?.branchIds) && payload.branchIds.length > 0) {
            branchId = payload.branchIds[0]
          }
        }
      } catch {
        // ignore
      }
    }

    const hasExplicitNoBranchHeader =
      config.headers['x-branch-id'] === null ||
      config.headers['x-branch-id'] === false ||
      config.headers['x-branch-id'] === 'none' ||
      config.headers['X-Branch-Id'] === null ||
      config.headers['X-Branch-Id'] === false ||
      config.headers['X-Branch-Id'] === 'none'

    const cleanBranchId = typeof branchId === 'string' ? branchId.trim() : ''

    if (hasExplicitNoBranchHeader || !cleanBranchId || cleanBranchId.toLowerCase() === 'none' || cleanBranchId.toLowerCase() === 'null' || cleanBranchId.toLowerCase() === 'undefined') {
      if (typeof config.headers.delete === 'function') {
        config.headers.delete('x-branch-id')
        config.headers.delete('X-Branch-Id')
      }
      delete config.headers['x-branch-id']
      delete config.headers['X-Branch-Id']
    } else {
      config.headers['x-branch-id'] = cleanBranchId
      if (typeof config.headers.delete === 'function') {
        config.headers.delete('X-Branch-Id')
      } else {
        delete config.headers['X-Branch-Id']
      }
    }

    const cleanOrgId = typeof orgId === 'string' ? orgId.trim() : ''
    if (cleanOrgId && cleanOrgId.toLowerCase() !== 'none' && cleanOrgId.toLowerCase() !== 'null' && cleanOrgId.toLowerCase() !== 'undefined') {
      config.headers['x-org-id'] = cleanOrgId
      if (typeof config.headers.delete === 'function') {
        config.headers.delete('X-Org-Id')
      } else {
        delete config.headers['X-Org-Id']
      }
    } else {
      if (typeof config.headers.delete === 'function') {
        config.headers.delete('x-org-id')
        config.headers.delete('X-Org-Id')
      } else {
        delete config.headers['x-org-id']
        delete config.headers['X-Org-Id']
      }
    }

    const existingAcademicYear =
      (config.headers['x-academic-year'] as string | undefined) ||
      (config.headers['X-Academic-Year'] as string | undefined)

    const getValidUuidFromStorage = () => {
      if (typeof localStorage === 'undefined') return undefined
      const candidates = [
        localStorage.getItem('academic_year_id'),
        localStorage.getItem('x_academic_year'),
        localStorage.getItem('academic_session_id'),
      ]
      for (const cand of candidates) {
        if (cand && isUuidHeader(cand)) {
          return cand.trim()
        }
      }
      return undefined
    }

    const storedAcademicYear = getValidUuidFromStorage()
    const zustandAcademicYear = isUuidHeader(useAcademicYearStore.getState().academicYearId)
      ? useAcademicYearStore.getState().academicYearId.trim()
      : undefined

    const academicYear =
      storedAcademicYear ||
      zustandAcademicYear ||
      (isUuidHeader(existingAcademicYear) ? existingAcademicYear!.trim() : undefined) ||
      DEFAULT_ACADEMIC_YEAR_ID

    const isClassReportsRequest = config.url ? config.url.includes('/api/class-reports') : false

    if (isClassReportsRequest) {
      const yearName =
        (existingAcademicYear && !isUuidHeader(existingAcademicYear) ? existingAcademicYear : undefined) ||
        useAcademicYearStore.getState().academicYearName ||
        (typeof localStorage !== 'undefined' ? localStorage.getItem('academic_year_name') : '')

      if (typeof config.headers.delete === 'function') {
        config.headers.delete('X-Academic-Session-Id')
        config.headers.delete('x-academic-session-id')
        config.headers.delete('X-Academic-Year-Id')
        config.headers.delete('x-academic-year-id')
        config.headers.delete('X-Session-Id')
        config.headers.delete('x-session-id')
      } else {
        delete config.headers['x-academic-session-id']
        delete config.headers['X-Academic-Session-Id']
        delete config.headers['x-academic-year-id']
        delete config.headers['X-Academic-Year-Id']
        delete config.headers['x-session-id']
        delete config.headers['X-Session-Id']
      }

      if (yearName) {
        config.headers['X-Academic-Year'] = yearName
        config.headers['x-academic-year'] = yearName
      } else {
        if (typeof config.headers.delete === 'function') {
          config.headers.delete('X-Academic-Year')
          config.headers.delete('x-academic-year')
        } else {
          delete config.headers['X-Academic-Year']
          delete config.headers['x-academic-year']
        }
      }
    } else if (!isAcademicSessionsRequest && !existingAcademicYear && academicYear && isUuidHeader(academicYear)) {
      config.headers['X-Academic-Year'] = academicYear
      config.headers['x-academic-year'] = academicYear
      config.headers['X-Academic-Year-Id'] = academicYear
      config.headers['x-academic-year-id'] = academicYear
      config.headers['X-Academic-Session-Id'] = academicYear
      config.headers['x-academic-session-id'] = academicYear
      config.headers['X-Session-Id'] = academicYear
      config.headers['x-session-id'] = academicYear
    }
  }

  sanitizeHeadersAndParams(config)



  return config
}

export const responseInterceptor = (response: AxiosResponse): AxiosResponse => {
  return response
}

export const errorInterceptor = async (error: AxiosError): Promise<any> => {
  const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

  const errData = error.response?.data as any
  const errStatus = error.response?.status
  const responseDataStr = JSON.stringify(errData || '').toLowerCase()

  const isAcademicSessionsEndpoint = Boolean(
    originalRequest?.url?.includes('/api/academic-sessions') ||
    originalRequest?.url?.includes('academic-sessions')
  )

  const rawErr = errData?.error
  const errCode = (
    typeof rawErr === 'string'
      ? rawErr
      : typeof rawErr?.error === 'string'
      ? rawErr.error
      : typeof rawErr?.code === 'string'
      ? rawErr.code
      : typeof errData?.code === 'string'
      ? errData.code
      : typeof errData?.error === 'string'
      ? errData.error
      : ''
  ).toUpperCase()

  const errMessageStr = (
    typeof errData?.message === 'string'
      ? errData.message
      : Array.isArray(errData?.message)
      ? errData.message.join(' ')
      : typeof rawErr?.message === 'string'
      ? rawErr.message
      : Array.isArray(rawErr?.message)
      ? rawErr.message.join(' ')
      : ''
  ).toUpperCase()

  const isAcademicYearError =
    !isAcademicSessionsEndpoint &&
    (errStatus === 400 || errStatus === 404 || errStatus === 409 || errStatus === 422) &&
    (errCode === 'ACADEMIC_YEAR_NOT_FOUND' ||
      errCode === 'ACADEMIC_YEAR_INACTIVE' ||
      errCode === 'ACADEMIC_YEAR_INVALID' ||
      errCode === 'ACADEMIC_YEAR_HEADER_REQUIRED' ||
      errMessageStr.includes('ACADEMIC_YEAR') ||
      errMessageStr.includes('X-ACADEMIC-YEAR') ||
      responseDataStr.includes('academic_year_not_found') ||
      responseDataStr.includes('academic_year_inactive') ||
      responseDataStr.includes('academic_year_invalid') ||
      responseDataStr.includes('academic_year_header_required') ||
      responseDataStr.includes('x-academic-year header is required') ||
      responseDataStr.includes('x-academic-year') ||
      responseDataStr.includes('inactive academic year'))

  if (isAcademicYearError) {
    console.warn('[Interceptors] Academic year error from backend. Evicting stale storage and resolving active session:', {
      status: errStatus,
      error: errCode || errMessageStr || errData?.error || errData?.message,
    })
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('academic_year_id')
      localStorage.removeItem('academic_session_id')
      localStorage.removeItem('x_academic_year')
      localStorage.removeItem('managed_academic_year')
    }
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem('academic_year_id')
      sessionStorage.removeItem('academic_session_id')
      sessionStorage.removeItem('x_academic_year')
    }

    if (originalRequest && !originalRequest._retry) {
      originalRequest._retry = true
      try {
        const freshAcademicYearId = (await useAcademicYearStore.getState().fetchActiveAcademicYear()) || (await getActiveAcademicYearId())
        if (freshAcademicYearId && originalRequest.headers) {
          if (typeof (originalRequest.headers as any).set === 'function') {
            ;(originalRequest.headers as any).set('X-Academic-Year', freshAcademicYearId)
            ;(originalRequest.headers as any).set('x-academic-year', freshAcademicYearId)
            ;(originalRequest.headers as any).set('X-Session-Id', freshAcademicYearId)
            ;(originalRequest.headers as any).set('x-session-id', freshAcademicYearId)
          } else {
            originalRequest.headers['X-Academic-Year'] = freshAcademicYearId
            originalRequest.headers['x-academic-year'] = freshAcademicYearId
            originalRequest.headers['X-Session-Id'] = freshAcademicYearId
            originalRequest.headers['x-session-id'] = freshAcademicYearId
          }
          useAcademicYearStore.getState().setAcademicYear(freshAcademicYearId)
          console.info('[Interceptors] Retrying failed request with resolved active academic year:', freshAcademicYearId)
          return axios.request(originalRequest)
        }
      } catch (retryErr) {
        console.error('[Interceptors] Failed to resolve active academic year for retry:', retryErr)
      }
    }
  }

  const status = error.response?.status
  const errDataStr = JSON.stringify(error.response?.data || {}).toLowerCase()

  const isTokenError =
    status === 401 ||
    errDataStr.includes('invalid_or_expired_token') ||
    errDataStr.includes('expired_token') ||
    errDataStr.includes('invalid_token') ||
    errDataStr.includes('jwt expired') ||
    errDataStr.includes('token_expired')

  if (isTokenError) {
    console.warn('[API Client] Invalid or expired token error detected for:', originalRequest?.url, 'Message:', errDataStr)
  }

  const isAuthEndpoint =
    originalRequest?.url?.includes('/api/auth/login') ||
    originalRequest?.url?.includes('/api/auth/refresh') ||
    originalRequest?.url?.includes('/api/auth/logout') ||
    originalRequest?.url?.includes('/api/auth/tenant/login') ||
    originalRequest?.url?.includes('/api/auth/superadmin/login')

  if (isTokenError && originalRequest) {
    if (isAuthEndpoint) {
      if (originalRequest.url?.includes('/api/auth/refresh') || originalRequest.url?.includes('/api/auth/logout')) {
        console.warn('[API Client] Auth endpoint returned 401/429. Session expired. Evicting session without retry loop.')
        clearAuthTokens()
        useAuthStore.setState({ isAuthenticated: false, user: null, role: null, accessToken: null, permissions: [] })
        safeNavigateToLogin()
      }
      return Promise.reject(error)
    }

    if (!originalRequest._retry) {
      originalRequest._retry = true
      try {
        const newToken = await handleTokenRefresh()
        if (newToken && originalRequest.headers) {
          if (typeof (originalRequest.headers as any).set === 'function') {
            ;(originalRequest.headers as any).set('Authorization', `Bearer ${newToken}`)
          } else {
            originalRequest.headers.Authorization = `Bearer ${newToken}`
          }

          if (!originalRequest.baseURL) {
            originalRequest.baseURL = getApiBaseUrl()
          }

          console.info('[API Client] Retrying failed request after successful token refresh:', originalRequest.url)
          return axios.request(originalRequest)
        }
      } catch (refreshErr) {
        return Promise.reject(refreshErr)
      }
    }
  }

  return Promise.reject(error)
}

